import type {
  FormatDateOptions,
  FormatTimeOptions,
  TimeZone,
} from '@shared/utils/date';
import { toDate, userTimeZone } from '@shared/utils/date';
import type { ReactNode } from 'react';
import { DateTime } from './DateTime';

interface DateParts {
  year?: string;
  month?: string;
  day?: string;
}

export interface DateTimeRangeProps {
  from?: Date | string | null;
  to?: Date | string | null;
  date?: 'always' | 'never' | 'auto';
  time?: boolean;
  dateStyle?: FormatDateOptions['dateStyle'];
  timeStyle?: FormatTimeOptions['timeStyle'];
  timeZone?: TimeZone;
  empty?: ReactNode;
}

function getDateParts(date: Date | string, timeZone: string): DateParts {
  return Object.fromEntries(
    Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      timeZone,
      year: 'numeric',
    })
      .formatToParts(toDate(date))
      .map(({ type, value }) => [type, value])
  );
}

function isSameDayInTimeZone(
  from: Date | string,
  to: Date | string,
  timeZone: string
) {
  const fromParts = getDateParts(from, timeZone);
  const toParts = getDateParts(to, timeZone);

  return (
    fromParts.year === toParts.year &&
    fromParts.month === toParts.month &&
    fromParts.day === toParts.day
  );
}

export function DateTimeRange({
  from,
  to,
  date = 'auto',
  time = true,
  dateStyle,
  timeStyle = 'short',
  timeZone,
  empty = null,
}: DateTimeRangeProps) {
  if (!from && !to) {
    return empty;
  }

  const tz = timeZone ?? userTimeZone();
  const showFromDate = date === 'always' || date === 'auto';
  const sameDay = from && to ? isSameDayInTimeZone(from, to, tz) : false;
  const showToDate = date === 'always' || (date === 'auto' && !sameDay);

  return (
    <>
      {from && (
        <DateTime
          value={from}
          dateStyle={dateStyle}
          timeStyle={timeStyle}
          timeZone={tz}
          date={showFromDate}
          time={time}
        />
      )}{' '}
      &rarr;{' '}
      {to && (
        <DateTime
          value={to}
          dateStyle={dateStyle}
          timeStyle={timeStyle}
          timeZone={tz}
          date={showToDate}
          time={time}
        />
      )}
    </>
  );
}
